import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';
import MineralwasserMap from '../../components/MineralwasserMap';
import { 
  Utensils, 
  Languages, 
  MessageSquare,
  Volume2,
  BookOpen,
  ArrowRight,
  CheckCircle,
  AlertCircle,
  User,
  Users,
  Coffee,
  Wine,
  Salad,
  ChefHat,
  UtensilsCrossed,
  Globe,
  Lightbulb
} from 'lucide-react';

const HospitalityCareerPage = () => {
  const [activeTab, setActiveTab] = useState('staff');
  const [playingAudio, setPlayingAudio] = useState(null);

  // Text-to-speech function
  const speakGerman = (text, id) => {
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = 'de-DE';
      utterance.rate = 0.9;
      setPlayingAudio(id);
      utterance.onend = () => setPlayingAudio(null);
      window.speechSynthesis.speak(utterance);
    }
  };

  // Staff phrases (what you say)
  const staffPhrases = [
    { de: 'Was darf ich Ihnen anbieten?', en: 'What can I offer you?' },
    { de: 'Was hätten Sie gerne?', en: 'What would you like to have?' },
    { de: 'Was darf es sein?', en: 'What would you like?' },
    { de: 'Dürfte ich Ihre Bestellung aufnehmen?', en: 'Could I take your order?' },
    { de: 'Die Ente mit Reis kann ich Ihnen empfehlen', en: 'I can recommend you the duck with rice' },
    { de: 'Könntest du bitte den Stuhl bringen?', en: 'Could you please bring the chair?' },
    { de: 'Hi, mein Name ist Francis. Ich werde Sie heute bedienen.', en: 'Hi, my name is Francis. I will serve you today.' },
    { de: 'Haben Sie eine Reservierung?', en: 'Do you have a reservation?' },
    { de: 'Auf welchen Namen haben Sie reserviert?', en: 'For which name did you reserve?' },
    { de: 'Dürfte ich Ihnen die Jacke abnehmen?', en: 'Could I take your coat?' },
    { de: 'Dann dürfen Sie mir einmal kurz folgen', en: 'Then you can follow me quickly' },
    { de: 'Vorsicht, heiß!', en: 'Careful, hot!' },
    { de: 'Dürfte ich Ihren Teller abräumen?', en: 'Could I clear your plate?' },
    { de: 'Gleichfalls', en: 'Same to you' },
    { de: 'Stimmt alles bei Ihnen?', en: 'Is everything alright?' },
    { de: 'Schmeckt Ihnen das Essen?', en: 'Do you enjoy your meal?' },
    { de: 'Wie möchten Sie bezahlen?', en: 'How would you like to pay?' },
    { de: 'Bar oder mit Karte?', en: 'Cash or with card?' },
    { de: 'Es dauert circa 15 Minuten', en: 'It will take approximately 15 minutes' },
    { de: 'Kommt sofort', en: 'Coming right up' },
  ];

  // Customer phrases (what customers say)
  const customerPhrases = [
    { de: 'Ich hätte gerne eine Spezi', en: 'I would like to have a Spezi (Fanta + Coke)' },
    { de: 'Ich hätte gerne die 24', en: 'I would like to have number 24' },
    { de: 'Können Sie mir etwas empfehlen?', en: 'Could you recommend something to me?' },
    { de: 'Ein Tisch für vier bitte', en: 'A table for four please' },
    { de: 'Könnten wir an diesem Tisch sitzen?', en: 'Could we sit at this table?' },
    { de: 'Ich hätte gerne die Speisekarte', en: 'I would like to have the menu please' },
    { de: 'Ich nehme das Gleiche', en: 'I\'ll have the same' },
    { de: 'Könnte ich bitte die Rechnung haben?', en: 'Could I have the bill please?' },
    { de: 'Ich glaube, Sie haben einen Fehler gemacht', en: 'I believe you made a mistake' },
    { de: 'Stimmt so', en: 'Keep the change (tip)' },
    { de: 'Das stimmt so', en: 'That\'s correct (keep the rest)' },
    { de: 'Machen wir 45', en: 'Let\'s make it 45 (tipping up)' },
  ];

  // Dietary restrictions
  const dietaryRestrictions = [
    { de: 'Vegetarisch', en: 'Vegetarian - no meat', icon: '🥗' },
    { de: 'Vegan', en: 'No animal products (meat, milk, eggs)', icon: '🌱' },
    { de: 'Glutenfrei', en: 'No gluten/dough products', icon: '🌾' },
    { de: 'Laktosefrei', en: 'No lactose (milk sugar)', icon: '🥛' },
    { de: 'Ohne...', en: 'Without... (e.g., Ohne Zwiebeln - without onions)', icon: '❌' },
    { de: 'Scharf', en: 'Spicy (most Germans don\'t like spicy!)', icon: '🌶️' },
    { de: 'Nüsse', en: 'Nuts (common allergy)', icon: '🥜' },
    { de: 'Das Fleisch', en: 'Meat', icon: '🥩' },
  ];

  // Restaurant vocabulary
  const restaurantVocab = [
    { de: 'Der Mittagstisch', en: 'Special lunch menu', category: 'general' },
    { de: 'Prost!', en: 'Cheers! (look in the eyes!)', category: 'general' },
    { de: 'Das Besteck', en: 'Cutlery', category: 'tableware' },
    { de: 'Die Speisekarte', en: 'Menu', category: 'general' },
    { de: 'Das Messer', en: 'Knife', category: 'tableware' },
    { de: 'Der Löffel', en: 'Spoon', category: 'tableware' },
    { de: 'Die Gabel', en: 'Fork', category: 'tableware' },
    { de: 'Der Teller', en: 'Plate', category: 'tableware' },
    { de: 'Der Tisch', en: 'Table', category: 'furniture' },
    { de: 'Die Tischdecke', en: 'Tablecloth', category: 'furniture' },
    { de: 'Der Stuhl, die Stühle', en: 'Chair, chairs', category: 'furniture' },
    { de: 'Die Kerze', en: 'Candle', category: 'furniture' },
    { de: 'Das Büffet', en: 'Buffet', category: 'general' },
    { de: 'Der Wein', en: 'Wine', category: 'drinks' },
    { de: 'Das Bier', en: 'Beer', category: 'drinks' },
    { de: 'Das Wasser', en: 'Water', category: 'drinks' },
    { de: 'Das Mineralwasser', en: 'Sparkling water (also: Sprudel)', category: 'drinks' },
    { de: 'Das Fest', en: 'Party/celebration', category: 'general' },
    { de: 'Die Vorspeise', en: 'Appetizer', category: 'menu' },
    { de: 'Die Hauptspeise', en: 'Main dish', category: 'menu' },
    { de: 'Die Nachspeise', en: 'Dessert', category: 'menu' },
    { de: 'Die Abholung', en: 'Take away', category: 'general' },
    { de: 'Die Lieferung', en: 'Delivery', category: 'general' },
    { de: 'Drinnen', en: 'Inside', category: 'general' },
    { de: 'Draußen', en: 'Outside', category: 'general' },
    { de: 'Das Tagesgericht', en: 'Dish of the day', category: 'menu' },
    { de: 'Die Beilage', en: 'Side dish', category: 'menu' },
    { de: 'Die Serviette', en: 'Napkin', category: 'tableware' },
    { de: 'Das Salz', en: 'Salt', category: 'condiments' },
    { de: 'Der Pfeffer', en: 'Pepper', category: 'condiments' },
    { de: 'Der Zucker', en: 'Sugar', category: 'condiments' },
    { de: 'Das Öl', en: 'Oil', category: 'condiments' },
    { de: 'Der Essig', en: 'Vinegar', category: 'condiments' },
  ];

  // Drink measurements
  const drinkMeasurements = [
    { de: 'Ein Glas Wein', en: 'A glass of wine', note: 'NOT "ein Wein"' },
    { de: 'Eine Flasche Wasser', en: 'A bottle of water', note: 'NOT "ein Wasser"' },
    { de: 'Eine Tasse Kaffee', en: 'A cup of coffee', note: '' },
    { de: 'Ein Krug Bier', en: 'A pitcher of beer', note: '' },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-babyblue-50 via-babyblue-100/50 to-white">
      <Navbar />

      {/* Hero Section */}
      <section className="py-12 md:py-16 px-6 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-amber-100/30 rounded-full filter blur-3xl -translate-y-1/2 translate-x-1/2"></div>
        
        <div className="container mx-auto max-w-4xl relative z-10">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Utensils className="w-4 h-4" />
              Career Path
            </div>
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-babyblue-800 mb-2">
              Hospitality & Gastronomy
            </h1>
            <p className="text-xl text-babyblue-600 mb-6">Gastronomie & Hotel</p>
          </div>

          {/* Introduction */}
          <div className="bg-gradient-to-br from-amber-50 to-white rounded-2xl border border-amber-200 p-6 shadow-lg">
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <ChefHat className="w-7 h-7 text-amber-600" />
              </div>
              <div>
                <h2 className="font-heading text-2xl font-bold text-babyblue-800 mb-3">
                  The Future of Dining
                </h2>
                <p className="text-babyblue-600 leading-relaxed">
                  Eating outside is the future and Germany offers ideal variety in working in various sectors of services. 
                  We are a <strong className="text-babyblue-800">diverse country</strong> with many restaurants and diners – 
                  whether it's German, Turkish, Italian or Asian. Delicious food is what contributes to the culture of our country.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Communication Section */}
      <section className="py-12 px-6 bg-white">
        <div className="container mx-auto max-w-4xl">
          <div className="text-center mb-8">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-babyblue-800 mb-2">
              Communication
            </h2>
            <p className="text-babyblue-600">
              Understanding both verbal and non-verbal German communication is essential.
              Be open to diverse cultures – the German population is very diverse!
            </p>
          </div>

          {/* Grammar Tip */}
          <div className="bg-gradient-to-r from-blue-50 to-blue-50 rounded-xl p-5 border border-blue-200 mb-8">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center flex-shrink-0">
                <BookOpen className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="font-bold text-babyblue-800 mb-2">Grammar Tip: Konjunktiv II</h3>
                <p className="text-babyblue-600 text-sm mb-2">
                  One of the most important tenses is the <strong>Konjunktiv II</strong> – this is how Germans express <strong>politeness</strong>.
                </p>
                <p className="text-babyblue-600 text-sm">
                  <strong>Modalverben</strong> are super important: <span className="text-blue-600 font-medium">können, dürfen, müssen, mögen, sollen, wollen</span>
                </p>
              </div>
            </div>
          </div>

          {/* Phrase Tabs */}
          <div className="bg-white rounded-2xl border border-amber-200 overflow-hidden shadow-lg">
            <div className="flex border-b border-amber-200">
              <button
                onClick={() => setActiveTab('staff')}
                className={`flex-1 flex items-center justify-center gap-2 px-6 py-4 font-semibold transition-all ${
                  activeTab === 'staff'
                    ? 'bg-amber-500 text-white'
                    : 'bg-amber-50 text-babyblue-600 hover:bg-amber-100'
                }`}
              >
                <User className="w-5 h-5" />
                Staff Phrases
              </button>
              <button
                onClick={() => setActiveTab('customer')}
                className={`flex-1 flex items-center justify-center gap-2 px-6 py-4 font-semibold transition-all ${
                  activeTab === 'customer'
                    ? 'bg-amber-500 text-white'
                    : 'bg-amber-50 text-babyblue-600 hover:bg-amber-100'
                }`}
              >
                <Users className="w-5 h-5" />
                Customer Phrases
              </button>
            </div>

            <div className="p-6">
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {(activeTab === 'staff' ? staffPhrases : customerPhrases).map((phrase, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-amber-50 rounded-lg hover:bg-amber-100 transition-colors cursor-pointer" onClick={() => speakGerman(phrase.de, `${activeTab}-${index}`)} data-testid={`phrase-row-${activeTab}-${index}`}>
                    <div className="flex-1">
                      <p className="font-medium text-babyblue-800">{phrase.de}</p>
                      <p className="text-babyblue-500 text-sm">{phrase.en}</p>
                    </div>
                    <button
                      type="button"
                      className={`p-2 rounded-full transition-all ${
                        playingAudio === `${activeTab}-${index}` 
                          ? 'bg-amber-500 text-white' 
                          : 'bg-white text-amber-500 hover:bg-amber-500 hover:text-white'
                      }`}
                    >
                      <Volume2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Dietary Restrictions */}
      <section className="py-12 px-6 bg-gradient-to-b from-amber-50 to-white">
        <div className="container mx-auto max-w-4xl">
          <div className="text-center mb-8">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-babyblue-800 mb-2">
              Dietary Restrictions
            </h2>
            <p className="text-babyblue-600">
              Germany has many individualistic lifestyles – every person has different needs and wishes for eating.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {dietaryRestrictions.map((item, index) => (
              <div 
                key={index} 
                className="bg-white rounded-xl p-4 border border-babyblue-200 flex items-center gap-4 group hover:border-amber-400 hover:shadow-md transition-all cursor-pointer"
                onClick={() => speakGerman(item.de, `diet-${index}`)}
              >
                <span className="text-3xl">{item.icon}</span>
                <div className="flex-1">
                  <p className="font-bold text-babyblue-800">{item.de}</p>
                  <p className="text-babyblue-500 text-sm">{item.en}</p>
                </div>
                <div className={`p-2 rounded-full transition-all ${
                  playingAudio === `diet-${index}` 
                    ? 'bg-amber-500 text-white' 
                    : 'bg-amber-100 text-amber-500 opacity-0 group-hover:opacity-100'
                }`}>
                  <Volume2 className="w-4 h-4" />
                </div>
              </div>
            ))}
          </div>

          {/* Fun Fact */}
          <div className="mt-6 bg-blue-50 rounded-xl p-4 border border-blue-200">
            <div className="flex items-start gap-3">
              <Lightbulb className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-blue-800">Fun Fact</p>
                <p className="text-blue-700 text-sm">
                  Some Germans say "<strong>mit ohne Zwiebeln, bitte</strong>" which literally means "with without onions, please" – a funny colloquial expression!
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Vocabulary Section */}
      <section className="py-12 px-6 bg-white">
        <div className="container mx-auto max-w-5xl">
          <div className="text-center mb-8">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-babyblue-800 mb-2">
              Restaurant Vocabulary
            </h2>
            <p className="text-babyblue-600">Essential words for working in hospitality</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {restaurantVocab.map((word, index) => (
              <div 
                key={index} 
                className="bg-amber-50 rounded-xl p-4 border border-amber-200 flex items-center justify-between group hover:border-amber-400 hover:shadow-md transition-all cursor-pointer"
                onClick={() => speakGerman(word.de, `vocab-${index}`)}
              >
                <div>
                  <p className="font-semibold text-babyblue-800">{word.de}</p>
                  <p className="text-babyblue-500 text-sm">{word.en}</p>
                </div>
                <div className={`p-2 rounded-full transition-all ${
                  playingAudio === `vocab-${index}` 
                    ? 'bg-amber-500 text-white' 
                    : 'bg-white text-amber-500 opacity-0 group-hover:opacity-100'
                }`}>
                  <Volume2 className="w-4 h-4" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Drink Measurements */}
      <section className="py-12 px-6 bg-gradient-to-b from-amber-50 to-white">
        <div className="container mx-auto max-w-4xl">
          <div className="text-center mb-8">
            <h2 className="font-heading text-2xl font-bold text-babyblue-800 mb-2">
              Measuring Drinks
            </h2>
            <p className="text-babyblue-600">
              With unmeasured items like drinks, you must include a measuring noun!
            </p>
          </div>

          <div className="bg-white rounded-2xl border border-amber-200 p-6 shadow-lg">
            <div className="flex items-start gap-3 mb-6 p-4 bg-red-50 rounded-lg border border-red-200">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-red-800">
                <strong>Never say:</strong> "Ein Wein, bitte" ❌<br />
                <strong>Always say:</strong> "Ein Glas Wein, bitte" ✓
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {drinkMeasurements.map((item, index) => (
                <div 
                  key={index} 
                  className="bg-green-50 rounded-xl p-4 border border-green-200 cursor-pointer hover:shadow-md transition-all"
                  onClick={() => speakGerman(item.de, `measure-${index}`)}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-bold text-babyblue-800">{item.de}</p>
                      <p className="text-babyblue-500 text-sm">{item.en}</p>
                      {item.note && <p className="text-red-500 text-xs mt-1">{item.note}</p>}
                    </div>
                    <div className={`p-2 rounded-full ${
                      playingAudio === `measure-${index}` ? 'bg-green-500 text-white' : 'bg-white text-green-500'
                    }`}>
                      <Volume2 className="w-4 h-4" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Regional Variations */}
      <section className="py-12 px-6 bg-white">
        <div className="container mx-auto max-w-4xl">
          <div className="bg-gradient-to-br from-blue-50 to-blue-50 rounded-2xl p-6 border border-blue-200">
            <div className="flex items-center gap-3 mb-4">
              <Globe className="w-6 h-6 text-blue-500" />
              <h3 className="font-heading text-xl font-bold text-babyblue-800">Regional Variations</h3>
            </div>
            
            <div className="space-y-3 text-babyblue-600">
              <p>
                <strong className="text-babyblue-800">Mineralwasser:</strong> In Western Germany (except Schleswig-Holstein), 
                the term "<strong>Sprudel</strong>" is also used. In some regions of Baden-Württemberg, 
                "<strong>Sauer Sprudel</strong>" is said as well.
              </p>
              <p>
                <strong className="text-babyblue-800">Switzerland & Austria:</strong> They use the informal term "<strong>Mineral</strong>". 
                In Vienna, the word "<strong>Soda</strong>" applies as well due to the high rise of tourism.
              </p>
            </div>

            <div className="mt-4 p-4 bg-amber-50 rounded-lg border border-amber-200">
              <div className="flex items-start gap-3">
                <Lightbulb className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-amber-800">Prost! 🍻</p>
                  <p className="text-amber-700 text-sm">
                    It is considered rude in Germany to NOT look each other in the eyes while saying "Prost"!
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 px-6 bg-gradient-to-r from-amber-500 to-amber-400">
        <div className="container mx-auto max-w-3xl text-center">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-white mb-4">
            Ready to Start Your Hospitality Career?
          </h2>
          <p className="text-white/90 mb-6">
            Continue learning with more vocabulary and practice exercises.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/vocabulary"
              className="bg-white text-amber-600 hover:bg-amber-50 px-6 py-3 rounded-full font-semibold inline-flex items-center justify-center gap-2 transition-all"
            >
              More Vocabulary
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link 
              to="/grammar"
              className="bg-white/20 text-white hover:bg-white/30 px-6 py-3 rounded-full font-semibold inline-flex items-center justify-center gap-2 transition-all border border-white/30"
            >
              Learn Konjunktiv II
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Other Career Paths */}
      <section className="py-12 px-6 bg-gradient-to-b from-white to-babyblue-50">
        <div className="container mx-auto max-w-6xl">
          <MineralwasserMap />
        </div>
      </section>

      {/* Other Career Paths */}
      <section className="py-12 px-6 bg-white">
        <div className="container mx-auto max-w-4xl">
          <h2 className="font-heading text-xl font-bold text-babyblue-800 mb-6 text-center">
            Explore Other Career Paths
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              { icon: '🏥', title: 'Healthcare', path: '/careers/healthcare' },
              { icon: '💻', title: 'IT & Technology', path: '/careers/it' },
              { icon: '🔧', title: 'Engineering', path: '/careers/engineering' },
            ].map((item, index) => (
              <Link 
                key={index}
                to={item.path}
                className="bg-babyblue-50 rounded-xl border border-babyblue-200 p-4 flex items-center gap-3 hover:border-primary hover:shadow-md transition-all"
              >
                <span className="text-2xl">{item.icon}</span>
                <span className="text-babyblue-700 font-medium">{item.title}</span>
                <ArrowRight className="w-4 h-4 text-babyblue-400 ml-auto" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HospitalityCareerPage;
